import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useCallback, useState } from "react";

import { CopyButton } from "@components";

const isWindows = () =>
  typeof navigator !== "undefined" && /Windows/i.test(navigator.userAgent);

/**
 * Extract help to show when a .tar download starts. `show()` opens it only on
 * Windows; render `dialog` where the help should appear.
 */
export function useExtractTarHelp() {
  const [open, setOpen] = useState(false);
  const show = useCallback(() => {
    if (isWindows()) {
      setOpen(true);
    }
  }, []);
  return {
    show,
    dialog: open ? <ExtractTarDialog onClose={() => setOpen(false)} /> : null,
  };
}

const ARCHIVE_PLACEHOLDER = "YOUR_FILE.tar";

const Command = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  padding: theme.spacing(0.5, 0.5, 0.5, 1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.action.hover,
  fontFamily: "monospace",
  overflowWrap: "anywhere",
}));

// Explorer can't open .tar files before Windows 11 23H2, but every supported
// Windows version ships tar.exe, so the command works without installing
// anything.
//
// The command uses a placeholder because the browser picks the saved name from
// the server's response, which the page can't read for a plain navigation.
export function ExtractTarDialog(props: { onClose: () => void }) {
  const command = `tar -xf "${ARCHIVE_PLACEHOLDER}"`;
  return (
    <Dialog open onClose={props.onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Extracting the .tar file</DialogTitle>
      <DialogContent>
        <DialogContentText>
          If double-clicking the file doesn&apos;t show the files inside, open a
          terminal in the folder you saved it to and run the command below,
          replacing {ARCHIVE_PLACEHOLDER} with the name of the file you
          downloaded:
        </DialogContentText>
        <Command>
          <span>{command}</span>
          <CopyButton text={command} tooltipText="Copy command" />
        </Command>
        <DialogContentText sx={{ mt: 2 }}>
          You can also extract it with 7-Zip.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={props.onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
